/**********************************************************************************************************
 *   BASE IMPORTS
 **********************************************************************************************************/
import { useEffect, useRef } from 'react'
import { useNavigate } from 'react-router-dom'
import { useAppSelector } from 'store/hooks'
import { Field, Formik, FormikProps } from 'formik'
import * as Yup from 'yup'

/**********************************************************************************************************
 *   SHARED IMPORTS
 **********************************************************************************************************/
import { Anchor, Button, Form as NXUIForm } from '@nexigendigital/nxui'

import { useVerifyTwoFactorMutation, useLogoutMutation, useRequestTwoFactorCodeQuery } from 'api/app'

/**********************************************************************************************************
 *   STYLE IMPORTS
 **********************************************************************************************************/
import { TwoFactorSelect } from './form.style'

/**********************************************************************************************************
 *   COMPONENT START
 **********************************************************************************************************/
export default function TwoFactor() {
    const navigate = useNavigate()
    const {
        appSession: { two_factor_required },
        appAuthentication: { twofactor: { primary } }
    } = useAppSelector((state) => state.app)

    /*   REFS
    *****************************************************/
    const formRef = useRef<FormikProps<{ code: string | null }>>(null)

    /*   QUERIES / MUTATIONS
    *****************************************************/
    useRequestTwoFactorCodeQuery(!primary, { skip: !!primary })

    const [verifyTwoFactor, { isError: verifyTwoFactorError, isSuccess: verifyTwoFactorSuccess, data: verifyTwoFactorData }] = useVerifyTwoFactorMutation()
    const [logout] = useLogoutMutation()

    /*   FUNCTIONS
    *****************************************************/
    function back() {
        logout()
        navigate('/login', { replace: true })
    }

    function redirectToAlternate() {
        navigate('/login/account-recovery', { replace: true })
    }

    /*   EFFECTS
     *****************************************************/
    useEffect(() => {
        if (!two_factor_required || verifyTwoFactorSuccess) navigate('/login/accounts')

        if ((verifyTwoFactorError || verifyTwoFactorData?.twofactor?.error) && formRef.current) {
            formRef.current.setSubmitting(false)
        }
    }, [navigate, verifyTwoFactorSuccess, verifyTwoFactorError, verifyTwoFactorData, two_factor_required])

    /*   RENDER COMPONENT
    *****************************************************/
    return (
        <>
            <TwoFactorSelect.Container>
                <Formik
                    innerRef={formRef}
                    initialValues={{
                        code: ''
                    }}
                    validationSchema={Yup.object({
                        code: Yup.string().required('Required')
                    })}
                    onSubmit={({ code }) => {
                        verifyTwoFactor({ code, type: primary!.type })
                    }}
                >
                    {({ isSubmitting }) => {
                        return (
                            <TwoFactorSelect.Form>
                                <TwoFactorSelect.Body>
                                    <Field
                                        label={'Authentication Code'}
                                        name={'code'}
                                        type={'text'}
                                        component={NXUIForm.InputField}
                                        disabled={isSubmitting}
                                    />
                                </TwoFactorSelect.Body>
                                <TwoFactorSelect.Footer>
                                    <div>
                                        <Anchor type={'button'} color={'primary'}
                                            onClick={redirectToAlternate}>
                                            Have an issue?
                                        </Anchor>
                                    </div>
                                    <div>
                                        <Button type={'button'} color={'secondary'} onClick={back} disabled={isSubmitting}>Back</Button>
                                        <Button
                                            color={'primary'}
                                            type={'submit'}
                                            loading={isSubmitting ? 'Verifying...' : false}
                                        >
                                            Verify
                                        </Button>
                                    </div>
                                </TwoFactorSelect.Footer>
                            </TwoFactorSelect.Form>
                        )
                    }}
                </Formik>
            </TwoFactorSelect.Container>
        </>
    )
}
